import Dialog from "@components/dialog/Dialog";
import { IFieldDef, IGetValueArgs } from "@components/smart/FieldInfo";
import SmartFastEntryList, { addNewLineItem, ISmartFastEntriesActionEvent } from "@components/smart/smartFastEntryList";
import { ISmartFieldBlur, ISmartFieldChange } from "@components/smart/smartField/SmartField";
import {
    DocumentEntity,
    DocumentItemEntity,
    GrantAssignmentDivisionEntity,
    IGrantAssignmentDivisionEntity,
    IPaymentDocumentItemEntity,
    SplitAccountAssignmentEntity
} from "@odata/GeneratedEntityTypes";
import { CurrencyCode, GrantAssignmentTypeCode } from "@odata/GeneratedEnums";
import { GrantSplitDialogHeader, GrantSplitDialogHeaderValue } from "@pages/grant/Grant.styles";
import { hasAnyItemDefaultGrant, IGrantSplitDialogData } from "@pages/grant/Grant.utils";
import { isVatRegisteredCompany } from "@utils/CompanyUtils";
import { roundToDecimalPlaces } from "@utils/general";
import { logger } from "@utils/log";
import Big from "big.js";
import i18next from "i18next";
import { cloneDeep } from "lodash";
import React, { ReactElement } from "react";
import { DefaultTheme } from "styled-components";

import { TRecordAny } from "../../global.types";
import BindingContext, { IEntity } from "../../odata/BindingContext";
import TestIds from "../../testIds";
import { formatCurrency } from "../../types/Currency";
import NumberType from "../../types/Number";
import { FormStorage } from "../../views/formView/FormStorage";
import ConfirmationButtons from "../../views/table/ConfirmationButtons";

interface IProps {
    storage: FormStorage;
    bindingContext: BindingContext;
    isItems: boolean;
}

export const PARTICIPATION_PATH = BindingContext.localContext("participation");

class GrantSplitDialog extends React.Component<IProps> {
    // flag for change, in case user just hit "close" button, this prevents draft change to occur
    _hasChanged = false;
    _addedLine = false;
    origData: SplitAccountAssignmentEntity[] = [];

    sum: number;

    constructor(props: IProps) {
        super(props);
        this.origData = cloneDeep(props.storage.getValue(props.bindingContext));
        const currentGrant = props.storage.getValue(props.bindingContext.getParent())?.Grant;
        const path = props.bindingContext.getNavigationPath();

        if (currentGrant?.IsClosed && !this.AllowClosedGrants) {
            this._addedLine = true;
            const defaultData: IEntity = {
                [GrantAssignmentDivisionEntity.Grant]: {
                    Id: currentGrant?.Id,
                    ShortName: currentGrant?.ShortName,
                    IsClosed: true
                },
                [GrantAssignmentDivisionEntity.Percentage]: 100
            };

            if (this.props.isItems) {
                defaultData[GrantAssignmentDivisionEntity.TransactionAmount] = this.Amount;
            }
            addNewLineItem(this.Storage, path, path, this.getFields(), defaultData);
        } else {

            const row = props.storage.getValue(props.bindingContext.getParent()) ?? {}; // DocumentGrantAssignment
            const isSplit = row.GrantAssignmentTypeCode === GrantAssignmentTypeCode.Division;

            if (!isSplit) {
                row.Divisions = [];
            }

            if (!row.Divisions?.length) {
                this._addedLine = true;
                addNewLineItem(this.Storage, path, path, this.getFields());
            }
        }
    }

    get itemBc() {
        return this.props.bindingContext.getParentCollection();
    }

    get itemTotalAmountBc() {
        const date = this.Storage.data.entity.DateVatDeduction;
        const isVat = isVatRegisteredCompany(this.Storage.context, date);
        const isValid = this.Storage.isValidPath(DocumentItemEntity.TransactionAmountNet); // BankTransactions don't have TransactionAmountNet
        const name = isValid && isVat ? DocumentItemEntity.TransactionAmountNet : DocumentItemEntity.TransactionAmount;
        return this.itemBc.navigate(name);
    }

    get Amount(): number {
        const totalAmount = Number(this.Storage.getValue(this.itemTotalAmountBc));
        if (isNaN(totalAmount)) {
            logger.error("GrantSplitDialog - total amount from item is not a number");
        }
        return isNaN(totalAmount) ? 0 : totalAmount;
    }

    get currencyCode() {
        return this.Storage.getValueByPath(DocumentEntity.TransactionCurrency)?.Code || CurrencyCode.CzechKoruna;
    }

    get Storage() {
        return this.props.storage;
    }

    get AllowClosedGrants() {
        return !this.props.isItems && !hasAnyItemDefaultGrant(this.Storage);
    }

    getFields = (addParticipation = true) => {
        const fields: IFieldDef[] = [
            { id: GrantAssignmentDivisionEntity.Grant },
            { id: GrantAssignmentDivisionEntity.Percentage }
        ];


        if (this.props.isItems) {
            fields.push({ id: GrantAssignmentDivisionEntity.TransactionAmount });
            if (addParticipation) {
                fields.push({ id: PARTICIPATION_PATH });
            }
        }

        return fields;
    }

    handlePercentageBlur = (args: ISmartFieldBlur) => {
        if (args.wasChanged && args.bindingContext.getPath() === GrantAssignmentDivisionEntity.Percentage) {
            const value = this.Storage.getValue(args.bindingContext);
            if (value < 0) {
                this.Storage.setValue(args.bindingContext, Math.abs(value));
            }

            if (this.props.isItems) {
                this.recalculateAmount(args.bindingContext);
            }
        }
    }

    handleAmountBlur = (args: ISmartFieldBlur) => {
        if (args.wasChanged && args.bindingContext.getPath() === GrantAssignmentDivisionEntity.TransactionAmount) {
            const value = this.Storage.getValue(args.bindingContext);
            const amount = this.Amount;
            let realValue: number = value;

            if ((value < 0 && amount > 0) || (value > 0 && amount < 0)) {
                realValue *= -1;
                this.Storage.setValue(args.bindingContext, realValue);
            }

            this.recalculatePercentage(args.bindingContext);
        }

    }

    handleGrantChange = (args: ISmartFieldChange) => {
        if (args.bindingContext.getPath() === GrantAssignmentDivisionEntity.Grant) {
            // refresh submit button
            this.forceUpdate();
        }
    }

    handleBlur = async (args: ISmartFieldBlur): Promise<void> => {
        this.handleAmountBlur(args);
        this.handlePercentageBlur(args);
        await this.Storage.handleBlur(args);
        this.Storage.refreshFields();

        // refresh submit button - after validation in storage.handleblur
        const path = args.bindingContext.getPath();
        if (path === GrantAssignmentDivisionEntity.Percentage || path === GrantAssignmentDivisionEntity.TransactionAmount) {
            this.forceUpdate();
        }
    };

    handleChange = (args: ISmartFieldChange): void => {
        this._hasChanged = true;
        this.Storage.handleChange(args);
        this.handleGrantChange(args);
        this.Storage.refreshFields();
    };

    recalculateAmount = (bc: BindingContext) => {
        if (!this.Storage.validateFieldSync(bc)) {
            const newRatio = Number(this.Storage.getValue(bc));
            if (isNaN(newRatio)) {
                logger.error("GrantSplitDialog - new ratio is not a number");
                return;
            }
            let newDivisionAmount = 0;
            if (newRatio !== 0) {
                newDivisionAmount = roundToDecimalPlaces(2, new Big(this.Amount).mul(newRatio).div(100).toNumber());
            }
            this.Storage.setValue(bc.getParent().navigate(GrantAssignmentDivisionEntity.TransactionAmount), newDivisionAmount);
        }
    }

    recalculatePercentage = (bc: BindingContext) => {
        if (!this.Storage.validateFieldSync(bc)) {
            const newAmount = Number(this.Storage.getValue(bc));
            if (isNaN(newAmount)) {
                return;
            }
            const newRatio = new Big(newAmount).div(this.Amount).mul(100).toNumber();
            this.Storage.setValue(bc.getParent().navigate(GrantAssignmentDivisionEntity.Percentage), newRatio);
        }
        this.Storage.addActiveField(bc.getParent().navigate(PARTICIPATION_PATH));
    }

    handleAction = (e: ISmartFastEntriesActionEvent<IPaymentDocumentItemEntity>): void => {
        this._hasChanged = true;
        this.Storage.handleLineItemsAction(e);

        this.sum = this.getPercentageSumValue();
        this.Storage.refresh();
    };

    isPercentageValid = (): boolean => {
        const sum = this.getPercentageSumValue();
        const row = this.Storage.getValue(this.props.bindingContext.getParent());

        return sum <= 100 && row?.Divisions?.length > 0;
    };

    getBindingContextsFromCollection = (rootBc: BindingContext, fields: string[]): BindingContext[] => {
        const data: IEntity[] = this.Storage.getValue(rootBc) || [];
        const bcs: BindingContext[] = [];

        for (const item of data) {
            const lineBc = rootBc.addKey(item);
            for (const field of fields) {
                bcs.push(lineBc.navigate(field));
            }
        }

        return bcs;
    };

    hasErrors = () => {
        const bcs: BindingContext[] = this.getBindingContextsFromCollection(this.props.bindingContext, this.getFields(false).map(field => field.id));
        let hasError = false;
        for (const bc of bcs) {
            hasError = this.Storage.hasError(bc) || hasError;

            const value = this.Storage.getValue(bc);
            hasError = (!value && value !== 0) || hasError;
        }

        return hasError;
    }

    validateFields = async (): Promise<boolean> => {
        const bcs: BindingContext[] = this.getBindingContextsFromCollection(this.props.bindingContext, this.getFields(false).map(field => field.id));
        let hasError = false;
        for (const bc of bcs) {
            hasError = !!(await this.Storage.validateField(bc)) || hasError;
        }

        return hasError;
    };

    handleConfirmDialog = async (): Promise<void> => {
        const hasError = await this.validateFields();
        const { storage } = this.props;

        if (hasError) {
            storage.refreshFields();
            return;
        }

        const item = storage.getValue(this.props.bindingContext.getParent());
        item.GrantAssignmentTypeCode = GrantAssignmentTypeCode.Division;
        item.Grant = {
            Id: GrantAssignmentTypeCode.Division
        };

        this.close();
    };

    handleCancel = (): void => {
        const { storage, bindingContext } = this.props;

        BindingContext.each(storage.getValue(bindingContext), bindingContext, (_, bc) => {
            storage.clearError(bc.navigate(GrantAssignmentDivisionEntity.TransactionAmount));
            storage.clearError(bc.navigate(GrantAssignmentDivisionEntity.Percentage));
            storage.clearError(bc.navigate(GrantAssignmentDivisionEntity.Grant));
            return true;
        }, true);

        if (this._hasChanged || this._addedLine) {
            // in case there were change or empty line was added, reset to original data
            storage.setValue(bindingContext, this.origData);
        }

        this.close();
    };

    close = (): void => {
        const storage = this.Storage as FormStorage<unknown, IGrantSplitDialogData>;
        storage.setCustomData({ isGrantSplitDialogOpened: false });
        storage.refresh();
    };

    getPercentageSumValue = (): number => {
        const row = this.Storage.getValue(this.props.bindingContext.getParent());

        return row.Divisions?.reduce((accumulator: number, object: IGrantAssignmentDivisionEntity) => {
            return accumulator + (object.Percentage || 0);
        }, 0) || 0;
    };

    getTotalAmountSum = (): number => {
        const divisions = this.Storage.getValue(this.props.bindingContext);
        const isNegative = this.Amount < 0;
        return divisions?.reduce((accumulator: number, object: IGrantAssignmentDivisionEntity) => {
            let amountToAdd = object.TransactionAmount || 0;
            if (isNegative) {
                amountToAdd = Math.min(0, amountToAdd);
            } else {
                amountToAdd = Math.max(0, amountToAdd);
            }
            return accumulator + amountToAdd;
        }, 0) ?? 0;
    };

    renderRemainsToDivide = (): ReactElement => {
        const isItems = this.props.isItems;
        const amount = isItems ? this.Amount : this.getPercentageSumValue();
        const remainsToDivide = isItems ? amount - this.getTotalAmountSum() : 100 - amount;

        let color: keyof DefaultTheme = "C_TEXT_primary";

        if (isItems) {
            if ((amount < 0 && remainsToDivide > 0) || (amount > 0 && remainsToDivide < 0)) {
                color = "C_SEM_text_bad";
            }
        } else {
            if (amount < 0 || amount > 100) {
                color = "C_SEM_text_bad";
            }
        }
        const label = isItems ? formatCurrency(remainsToDivide, this.currencyCode) :
            `${NumberType.format(roundToDecimalPlaces(2, remainsToDivide), {
                maximumFractionDigits: 2,
                minimumFractionDigits: 2
            })} %`;

        return <GrantSplitDialogHeader data-testid={TestIds.SplitDialogTitle}>
            {i18next.t("Grants:Document.RemainsToDivide")}:
            <GrantSplitDialogHeaderValue color={color} data-testid={TestIds.SplitDialogValue}>
                <b>{label}</b>
            </GrantSplitDialogHeaderValue>
        </GrantSplitDialogHeader>;
    };

    canConfirm = () => {
        const isPercentageValid = this.isPercentageValid();
        const hasErrors = this.hasErrors();

        return isPercentageValid && !hasErrors;
    }

    isItemDisabled = (args: IGetValueArgs, otherArgs?: TRecordAny) => {
        return this.AllowClosedGrants ? false : args.item?.Grant?.IsClosed;
    }

    render() {
        const title = this.props.isItems ? i18next.t("Grants:Document.ItemDivisionTitle") : i18next.t("Grants:Document.DivisionTitle");
        return (
            <Dialog
                title={title}
                isEditableWindow={false}
                width={this.props.isItems ? "740px" : "400px"}
                footer={
                    <ConfirmationButtons
                        confirmText={this.Storage.t("Common:General.Confirm")}
                        onConfirm={this.handleConfirmDialog}
                        isDisabled={!this.canConfirm()}
                        onCancel={this.handleCancel}
                        useWrapper={false}/>
                }
                onConfirm={this.handleConfirmDialog}
                onClose={this.handleCancel}>
                {this.renderRemainsToDivide()}
                <SmartFastEntryList
                    useLabelWrapping={true}
                    canAdd
                    isItemDisabled={this.isItemDisabled}
                    showChanges={false}
                    isCollapsible={false}
                    storage={this.Storage as FormStorage}
                    bindingContext={this.props.bindingContext}
                    columns={this.getFields()}
                    onBlur={this.handleBlur}
                    onChange={this.handleChange}
                    onAction={this.handleAction}
                    isItemCloneable={false}
                    order={null}
                />
            </Dialog>
        );
    }
}

export default GrantSplitDialog;